import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
  height,
  minHeight,
  minWidth,
  space,
  width,
} from 'styled-system';

import ButtonWithIcon, { DEFAULT_ICON_SIZE } from 'common/components/Button/ButtonWithIcon';
import { ResponsiveLazyImage } from 'common/components/LazyImage';
import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import { SmallHeading, Text } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { PhotoSection, TextSection } from '../../gql/query/types';
import { usePictureGuideContent } from '../../hooks/additional-info/picture-guide';
import type { BaseReclamationStepProps } from '../../types/steps';

interface ReclamationTakePictureGuideProps extends Pick<
  BaseReclamationStepProps,
  'setHeaderProps'
> {
  closeGuide: () => void;
  triggerAddPictureInput: Nullable<() => void>;
  backButtonLabel?: string;
}

const IMG_SIZE_S = 288;
const IMG_SIZE_M = 416;
const IMG_SIZE_L = 464;
const IMG_SIZE_XL = 568;

const RESPONSIVE_IMAGE_SIZES = [IMG_SIZE_S, IMG_SIZE_M, IMG_SIZE_L, IMG_SIZE_XL];

const UnstyledSubtitle = styled(SmallHeading)``;

const Subtitle = (props: ComponentProps<typeof UnstyledSubtitle>) => (
  <UnstyledSubtitle
    fontSize={['fs_16', null, 'fs_18']}
    mb="sp_8"
    pt="sp_8"
    color="sys.neutral.text.strong"
    {...props}
  />
);

const UnstyledParagraph = styled(Text)``;

const Paragraph = (props: ComponentProps<typeof UnstyledParagraph>) => (
  <UnstyledParagraph
    as="p"
    color="sys.neutral.text.default"
    fontSize={['fs_14', 'fs_16']}
    {...props}
  />
);

const UnstyledFigure = styled.figure<any>`
  ${space};
`;

const Figure = (props: ComponentProps<typeof UnstyledFigure>) => (
  <UnstyledFigure m="sp_0" {...props} />
);

const UnstyledCaption = styled(Text)``;

const Caption = (props: ComponentProps<typeof UnstyledCaption>) => (
  <UnstyledCaption
    as="figcaption"
    fontSize={['fs_12', 'fs_14']}
    color="sys.neutral.text.default"
    mb="sp_4"
    {...props}
  />
);

const UnstyledImage = styled.div<any>`
  ${width};
  ${minWidth};
  ${height};
  ${minHeight};
  ${space};
  ${backgroundRepeat};
  ${backgroundPosition};
  ${backgroundSize};
  opacity: ${({ show }) => `${Number(show)}`};
  transition: opacity 0.3s ease-in-out;
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage
    width={RESPONSIVE_IMAGE_SIZES}
    minWidth={RESPONSIVE_IMAGE_SIZES}
    height={RESPONSIVE_IMAGE_SIZES}
    minHeight={RESPONSIVE_IMAGE_SIZES}
    mb="sp_16"
    backgroundRepeat="no-repeat"
    backgroundPosition="center"
    backgroundSize="cover"
    role="img"
    style={{ backgroundImage: `url(${props.image})` }}
    {...props}
  />
);

const UnstyledButton = styled(ButtonWithIcon)<any>`
  ${width};
`;

const Button = (props: ComponentProps<typeof UnstyledButton>) => (
  <UnstyledButton
    iconSize={DEFAULT_ICON_SIZE}
    iconName="landscapeOutline"
    width={1}
    mt="sp_16"
    {...props}
  />
);

const renderTextSection = ({ heading, paragraph }: TextSection, index: number) => (
  <article key={index}>
    <Subtitle>{heading}</Subtitle>
    <Paragraph>{paragraph}</Paragraph>
  </article>
);

const renderPhotoSection = ({ photo }: PhotoSection, index: number) => (
  <ResponsiveLazyImage
    thumbnail={photo.thumbnail.url}
    x1={photo.x1.url}
    x2={photo.x2.url}
    x3={photo.x3.url}
    key={index}
  >
    {({ loaded, intersected, image, thumbnail, innerRef }) => (
      <Figure>
        <Caption>{photo.thumbnail.alt}</Caption>
        <Image
          ref={innerRef}
          show={intersected}
          image={loaded ? image : thumbnail}
          aria-label={photo.thumbnail.alt}
        />
      </Figure>
    )}
  </ResponsiveLazyImage>
);

const ReclamationTakePictureGuide = ({
  closeGuide,
  setHeaderProps,
  backButtonLabel,
  triggerAddPictureInput,
}: ReclamationTakePictureGuideProps) => {
  const [content, error] = usePictureGuideContent();
  const { t } = useLocalization();

  const handleButtonClick = () => {
    closeGuide();
    triggerAddPictureInput?.();
  };

  useEffect(() => {
    setHeaderProps({
      title: content?.title,
      buttonLabel: backButtonLabel ?? t('h24_go_back'),
      buttonIcon: 'arrowLeft',
      onClick: closeGuide,
    });
  }, [content]);

  return (
    <section data-testid="reclamation-take-picture-guide">
      {content && (
        <>
          <Paragraph>{content.description}</Paragraph>
          {content.textSectionDamage.map(renderTextSection)}
          {content.photoSectionDamage.map(renderPhotoSection)}
          {content.textSectionPackaging.map(renderTextSection)}
          {content.photoSectionPackaging.map(renderPhotoSection)}
          {content.textSectionToAvoid.map(renderTextSection)}
          {content.photoSectionToAvoid.map(renderPhotoSection)}
        </>
      )}
      {error && <ReclamationAlert message={error} />}
      <Button onClick={handleButtonClick}>{t('h24_add_picture')}</Button>
    </section>
  );
};

export default ReclamationTakePictureGuide;
